import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { RiArrowDownSLine } from '@remixicon/react';
import { useSession } from '@/lib/auth-client';
import { api, Database } from '@/lib/api';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface DatabaseSwitcherProps {
  currentDatabaseId: string;
  onSwitch?: (databaseId: string) => void;
}

export function DatabaseSwitcher({ currentDatabaseId, onSwitch }: DatabaseSwitcherProps) {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [databases, setDatabases] = useState<Database[]>([]);
  const [currentDatabase, setCurrentDatabase] = useState<Database | null>(null);

  useEffect(() => {
    if (session?.user?.id) {
      loadDatabases();
    }
  }, [session?.user?.id]);

  useEffect(() => {
    const db = databases.find((d) => d.id === currentDatabaseId);
    if (db) {
      setCurrentDatabase(db);
    }
  }, [currentDatabaseId, databases]);

  const loadDatabases = async () => {
    if (!session?.user?.id) return;
    try {
      const result = await api.listDatabases(session.user.id);
      setDatabases(result.databases);
    } catch (error) {
      console.error('Failed to load databases', error);
    }
  };

  const switchDatabase = (database: Database) => {
    if (onSwitch) {
      onSwitch(database.id);
    } else {
      navigate({ to: '/query/$id', params: { id: database.id } });
    }
  };

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem className="max-sm:hidden">
          <BreadcrumbLink href="/databases">Databases</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="text-border max-sm:hidden"> / </BreadcrumbSeparator>
        <BreadcrumbItem>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 font-medium text-foreground">
              {currentDatabase?.name ?? 'Select Database'}
              <RiArrowDownSLine className="-me-1 opacity-60" size={16} aria-hidden="true" />
            </DropdownMenuTrigger>
            <DropdownMenuContent sideOffset={10}>
              {databases.map((database) => (
                <DropdownMenuItem
                  key={database.id}
                  onClick={() => switchDatabase(database)}
                  className={database.id === currentDatabaseId ? 'bg-accent' : ''}
                >
                  {database.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}
